import type { Bounds, Point } from '../../../../core/shapes/models/ShapeViewModel';
import type {
  DistributionGuidesInput,
  DistributionGuidesOutput,
  DistributionOrientation,
  DistributionSnapDelta,
} from '../../models/DistributionGuides';

type AxisItem = {
  shapeId: string;
  bounds: Bounds;
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
};

type ActiveWindow = {
  a: AxisItem;
  b: AxisItem;
  c: AxisItem;
  movingRole: 'a' | 'b' | 'c';
};

const toAxisItem = (shapeId: string, bounds: Bounds): AxisItem => {
  const left = bounds.x;
  const top = bounds.y;
  const width = bounds.width;
  const height = bounds.height;
  const right = left + width;
  const bottom = top + height;
  const centerX = left + width / 2;
  const centerY = top + height / 2;
  return { shapeId, bounds, left, right, top, bottom, centerX, centerY, width, height };
};

const axisPosition = (item: AxisItem, orientation: DistributionOrientation): number => {
  return orientation === 'horizontal' ? item.left : item.top;
};

const isCollinear = (a: AxisItem, b: AxisItem, orientation: DistributionOrientation, tol: number): boolean => {
  if (orientation === 'horizontal') {
    return (
      Math.abs(a.top - b.top) <= tol ||
      Math.abs(a.centerY - b.centerY) <= tol ||
      Math.abs(a.bottom - b.bottom) <= tol
    );
  }
  return (
    Math.abs(a.left - b.left) <= tol ||
    Math.abs(a.centerX - b.centerX) <= tol ||
    Math.abs(a.right - b.right) <= tol
  );
};

const overlapAmount = (a1: number, a2: number, b1: number, b2: number): number => {
  const start = Math.max(Math.min(a1, a2), Math.min(b1, b2));
  const end = Math.min(Math.max(a1, a2), Math.max(b1, b2));
  return end - start;
};

const hasOrthoOverlap = (a: AxisItem, b: AxisItem, orientation: DistributionOrientation, minOverlap: number): boolean => {
  if (orientation === 'horizontal') {
    return overlapAmount(a.top, a.bottom, b.top, b.bottom) >= minOverlap;
  }
  return overlapAmount(a.left, a.right, b.left, b.right) >= minOverlap;
};

const axisGap = (a: AxisItem, b: AxisItem, orientation: DistributionOrientation): number => {
  if (orientation === 'horizontal') {
    return b.left - a.right;
  }
  return b.top - a.bottom;
};

const capByNearest = (
  items: AxisItem[],
  moving: AxisItem,
  orientation: DistributionOrientation,
  maxShapes: number
): AxisItem[] => {
  const maxStatic = Math.max(0, maxShapes - 1);
  if (items.length <= maxStatic) return items;
  const movingPos = orientation === 'horizontal' ? moving.centerX : moving.centerY;
  const sorted = [...items].sort((a, b) => {
    const aPos = orientation === 'horizontal' ? a.centerX : a.centerY;
    const bPos = orientation === 'horizontal' ? b.centerX : b.centerY;
    const da = Math.abs(aPos - movingPos);
    const db = Math.abs(bPos - movingPos);
    if (da !== db) return da - db;
    return a.shapeId.localeCompare(b.shapeId);
  });
  return sorted.slice(0, maxStatic);
};

const buildChain = (moving: AxisItem, statics: AxisItem[], orientation: DistributionOrientation): AxisItem[] => {
  return [moving, ...statics].sort((a, b) => {
    const da = axisPosition(a, orientation) - axisPosition(b, orientation);
    if (da !== 0) return da;
    return a.shapeId.localeCompare(b.shapeId);
  });
};

const computeGuideLineAnchor = (chain: AxisItem[], orientation: DistributionOrientation): number => {
  if (chain.length === 0) return 0;
  if (orientation === 'horizontal') {
    const top = Math.min(...chain.map((i) => i.top));
    return top - 12;
  }
  const left = Math.min(...chain.map((i) => i.left));
  return left - 12;
};

const segmentPoints = (
  a: AxisItem,
  b: AxisItem,
  orientation: DistributionOrientation,
  lineAnchor: number
): { start: Point; end: Point } => {
  if (orientation === 'horizontal') {
    return { start: { x: a.right, y: lineAnchor }, end: { x: b.left, y: lineAnchor } };
  }
  return { start: { x: lineAnchor, y: a.bottom }, end: { x: lineAnchor, y: b.top } };
};

const makeSegmentId = (orientation: DistributionOrientation, leftId: string, rightId: string): string => {
  return ['dist', orientation, leftId, rightId].join('|');
};

const pickActiveWindow = (
  chain: AxisItem[],
  movingId: string,
  orientation: DistributionOrientation,
  labelActivationDistance: number
): ActiveWindow | null => {
  const idx = chain.findIndex((c) => c.shapeId === movingId);
  if (idx < 0) return null;

  type Candidate = { w: ActiveWindow; diff: number };
  const candidates: Candidate[] = [];

  const addCandidate = (w: ActiveWindow) => {
    const g1 = axisGap(w.a, w.b, orientation);
    const g2 = axisGap(w.b, w.c, orientation);
    if (!(g1 > 0) || !(g2 > 0)) return;
    const diff = Math.abs(g1 - g2);
    if (diff > labelActivationDistance) return;
    candidates.push({ w, diff });
  };

  if (idx - 1 >= 0 && idx + 1 < chain.length) {
    addCandidate({ a: chain[idx - 1]!, b: chain[idx]!, c: chain[idx + 1]!, movingRole: 'b' });
  }

  if (idx - 2 >= 0) {
    addCandidate({ a: chain[idx - 2]!, b: chain[idx - 1]!, c: chain[idx]!, movingRole: 'c' });
  }

  if (idx + 2 < chain.length) {
    addCandidate({ a: chain[idx]!, b: chain[idx + 1]!, c: chain[idx + 2]!, movingRole: 'a' });
  }

  if (candidates.length === 0) return null;
  candidates.sort((x, y) => {
    if (x.diff !== y.diff) return x.diff - y.diff;
    if (x.w.movingRole !== y.w.movingRole) {
      const rank = (r: ActiveWindow['movingRole']) => (r === 'b' ? 0 : r === 'a' ? 1 : 2);
      return rank(x.w.movingRole) - rank(y.w.movingRole);
    }
    const ax = `${x.w.a.shapeId}|${x.w.b.shapeId}|${x.w.c.shapeId}`;
    const ay = `${y.w.a.shapeId}|${y.w.b.shapeId}|${y.w.c.shapeId}`;
    return ax.localeCompare(ay);
  });
  return candidates[0]!.w;
};

const computeWindowSnap = (w: ActiveWindow, orientation: DistributionOrientation, threshold: number): DistributionSnapDelta => {
  const g1 = axisGap(w.a, w.b, orientation);
  const g2 = axisGap(w.b, w.c, orientation);
  if (!(g1 > 0) || !(g2 > 0)) return { dx: 0, dy: 0 };
  const diff = Math.abs(g1 - g2);
  if (diff > threshold) return { dx: 0, dy: 0 };

  if (w.movingRole === 'a') {
    const target = g2;
    if (orientation === 'horizontal') {
      const desiredLeft = w.b.left - target - w.a.width;
      return { dx: desiredLeft - w.a.left, dy: 0 };
    }
    const desiredTop = w.b.top - target - w.a.height;
    return { dx: 0, dy: desiredTop - w.a.top };
  }

  if (w.movingRole === 'c') {
    const target = g1;
    if (orientation === 'horizontal') {
      const desiredLeft = w.b.right + target;
      return { dx: desiredLeft - w.c.left, dy: 0 };
    }
    const desiredTop = w.b.bottom + target;
    return { dx: 0, dy: desiredTop - w.c.top };
  }

  if (orientation === 'horizontal') {
    const span = w.c.left - w.a.right - w.b.width;
    if (!(span > 0)) return { dx: 0, dy: 0 };
    const desiredGap = span / 2;
    const desiredLeft = w.a.right + desiredGap;
    return { dx: desiredLeft - w.b.left, dy: 0 };
  }
  const span = w.c.top - w.a.bottom - w.b.height;
  if (!(span > 0)) return { dx: 0, dy: 0 };
  const desiredGap = span / 2;
  const desiredTop = w.a.bottom + desiredGap;
  return { dx: 0, dy: desiredTop - w.b.top };
};

export class DistributionGuidesService {
  static compute(input: DistributionGuidesInput): DistributionGuidesOutput {
    const {
      movingId,
      movingBounds,
      staticCandidates,
      orientation,
      threshold,
      maxShapes,
      labelActivationDistance,
      collinearTolerance,
    } = input;

    const moving = toAxisItem(movingId, movingBounds);
    const collinear = staticCandidates
      .map((c) => toAxisItem(c.shapeId, c.bounds))
      .filter(
        (c) =>
          isCollinear(c, moving, orientation, collinearTolerance) ||
          hasOrthoOverlap(c, moving, orientation, collinearTolerance)
      );

    const capped = capByNearest(collinear, moving, orientation, maxShapes);
    const chain = buildChain(moving, capped, orientation);

    if (chain.length < 3) {
      return { snapDelta: { dx: 0, dy: 0 }, segments: [] };
    }

    const window = pickActiveWindow(chain, movingId, orientation, labelActivationDistance);
    if (!window) {
      return { snapDelta: { dx: 0, dy: 0 }, segments: [] };
    }

    const snapDelta = computeWindowSnap(window, orientation, threshold);
    const lineAnchor = computeGuideLineAnchor([window.a, window.b, window.c], orientation);

    const segments: DistributionGuidesOutput['segments'] = [];
    const pairs: Array<[AxisItem, AxisItem]> = [
      [window.a, window.b],
      [window.b, window.c],
    ];
    pairs.forEach(([a, b]) => {
      const gap = axisGap(a, b, orientation);
      if (!(gap > 0)) return;
      const pts = segmentPoints(a, b, orientation, lineAnchor);
      segments.push({
        id: makeSegmentId(orientation, a.shapeId, b.shapeId),
        orientation,
        start: pts.start,
        end: pts.end,
        gap,
        betweenShapeIds: [a.shapeId, b.shapeId],
        showLabel: true,
      });
    });

    return { snapDelta, segments };
  }
}

